import { Children, Fragment, isValidElement, type ReactElement } from 'react';

import { Box, useTheme } from 'app/shopify/theme';

import type { AccordionItemProps } from './AccordionItem';

export type AccordionVariant = 'fullWidth' | 'containedOutline' | 'containedNeutral';

export interface AccordionProps {
  /** The three supported Type/Style combinations in Figma. */
  variant?: AccordionVariant;
  children: ReactElement<AccordionItemProps> | (ReactElement<AccordionItemProps> | null | false)[];
  testID?: string;
}

export function Accordion({ variant = 'fullWidth', children, testID }: AccordionProps) {
  const theme = useTheme();
  const contained = variant !== 'fullWidth';
  const items = Children.toArray(children).filter(isValidElement);

  return (
    <Box
      testID={testID}
      width="100%"
      borderRadius={contained ? 'radius.md' : 'radius.none'}
      paddingHorizontal={contained ? 'spacing.general.x4' : 'spacing.general.0'}
      backgroundColor={variant === 'containedNeutral' ? 'surface.primary-neutral' : undefined}
      overflow={contained ? 'hidden' : 'visible'}
    >
      {items.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && !contained ? (
            <Box backgroundColor="border.divider" height={Number(theme.tokens.sizing['sizing.general.x0,25'])} />
          ) : null}
          {item}
        </Fragment>
      ))}
      {variant === 'containedOutline' ? (
        // Figma's inside stroke must not add to the rows' width or height.
        <Box
          pointerEvents="none"
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          borderRadius="radius.md"
          borderColor="border.secondary"
          borderWidth={theme.borderWidths['border-width.subtle']}
        />
      ) : null}
    </Box>
  );
}
